import { useState, useCallback, ChangeEventHandler, useMemo } from 'react';

import useInputNavigation from './useInputNavigation';
import useIs12Hour from './useIs12Hour';
import {
  HOURS_ID,
  MINUTES_ID,
  TIME_PERIOD_ID,
  Times,
  InitialTime,
  TimePickerTime,
  TIME_PERIODS,
} from '../consts';
import {
  getTimePeriod,
  addHours,
  addMinutes,
  addTimePeriod,
  toDisplayHours,
  isPropControlled,
} from '../helpers';

export type UseTimePickerControlProps = {
  onChange?: (value?: TimePickerTime) => void;
  onEditDone?: (value?: TimePickerTime | undefined) => void;
  initialTime: InitialTime;
  disabled?: boolean;
  is12Hour?: boolean;
  value?: InitialTime;
};
const useTimePickerControl = ({
  onChange,
  onEditDone,
  initialTime,
  disabled,
  is12Hour: is12HourOverride,
  value,
}: UseTimePickerControlProps) => {
  const isControlled = isPropControlled(value);
  const is12Hour = useIs12Hour({ is12HourOverride });

  const [hasChangedTime, setHasChangedTime] = useState(false);
  const [hours, setHours] = useState(value?.hours || initialTime.hours);
  const [minutes, setMinutes] = useState(value?.minutes || initialTime.minutes);
  const [timePeriod, setTimePeriod] = useState(
    getTimePeriod({
      hours: value?.hours || initialTime.hours,
      is24Hour: !is12Hour,
    }),
  );

  const { hourInputProps, minuteInputProps, timePeriodInputProps, setFocused } =
    useInputNavigation();

  const hasTime = hours !== undefined && minutes !== undefined;

  const isPlaceholderTime = useCallback(
    () => hours === undefined || minutes === undefined,
    [hours, minutes],
  );

  const setMidnight = useCallback(() => {
    setHours(0);
    setMinutes(0);
    if (is12Hour) setTimePeriod(TIME_PERIODS.AM);
  }, [setHours, setMinutes, setTimePeriod, is12Hour]);

  const updateTime = useCallback(
    (times?: Times) => {
      if (isPlaceholderTime()) {
        setMidnight();
      }

      if (!times) return;
      if (times.hours !== undefined && times.hours > 23) return;
      if (times.minutes !== undefined && times.minutes > 59) return;

      let newHours = hours ?? 0;
      let newMinutes = minutes ?? 0;

      if (!isControlled) {
        if (times.hours !== undefined) {
          newHours = times.hours;
          setHours(newHours);
        }
        if (times.timePeriod !== undefined) {
          setTimePeriod(times.timePeriod);
        }
        if (times.minutes !== undefined) {
          newMinutes = times.minutes;
          setMinutes(newMinutes);
        }

        if (newHours > 11) {
          setTimePeriod(TIME_PERIODS.PM);
        } else {
          setTimePeriod(TIME_PERIODS.AM);
        }
      }

      setHasChangedTime(true);

      onChange?.({
        hours: newHours,
        minutes: newMinutes,
        is24Hour: !is12Hour,
      });
    },
    [
      onChange,
      isPlaceholderTime,
      setMidnight,
      setHours,
      setMinutes,
      setTimePeriod,
      hours,
      minutes,
      is12Hour,
      isControlled,
    ],
  );

  const adjustHours = useCallback(
    (toAdd: number) => {
      updateTime(
        addHours({
          hours,
          toAdd,
        }),
      );
    },
    [updateTime, hours],
  );

  const adjustMinutes = useCallback(
    (toAdd: number) => updateTime(addMinutes({ hours, minutes, toAdd })),
    [updateTime, hours, minutes],
  );

  const flipTimePeriod = useCallback(
    () => updateTime(addTimePeriod({ hours, timePeriod })),
    [updateTime, hours, timePeriod],
  );

  const setAmPeriod = useCallback(
    () => updateTime({ timePeriod: TIME_PERIODS.AM }),
    [updateTime],
  );

  const setPmPeriod = useCallback(
    () => updateTime({ timePeriod: TIME_PERIODS.PM }),
    [updateTime],
  );

  const hourOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      let value = (+e.currentTarget.value).toString(); // strip padding

      if (+value > 23) {
        value = value[value.length - 1];
      }
      updateTime({ hours: +value });
    },
    [updateTime],
  );

  const minuteOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      let value = (+e.currentTarget.value).toString(); // strip padding

      if (+value > 59) {
        value = value[value.length - 1];
      }
      updateTime({ minutes: +value });
    },
    [updateTime],
  );

  const incrementHour = useCallback(() => adjustHours(1), [adjustHours]);
  const decrementHour = useCallback(() => adjustHours(-1), [adjustHours]);
  const incrementMinute = useCallback(() => adjustMinutes(1), [adjustMinutes]);
  const decrementMinute = useCallback(() => adjustMinutes(-1), [adjustMinutes]);

  const inputOnBlur = useCallback(() => {
    if (hasChangedTime) {
      onEditDone?.({
        hours,
        minutes,
        is24Hour: !is12Hour,
      });

      setHasChangedTime(false);
    }
  }, [hasChangedTime, hours, is12Hour, minutes, onEditDone]);

  const sharedInputProps = useMemo(
    () => ({
      hasTime,
      disabled,
      setFocused,
      onBlur: inputOnBlur,
    }),
    [disabled, hasTime, inputOnBlur, setFocused],
  );

  return {
    getHourInputProps: useCallback(
      () => ({
        id: HOURS_ID,
        'data-testid': HOURS_ID,
        value: toDisplayHours({
          hours: isControlled ? value?.hours : hours,
          is24Hour: !is12Hour,
        }),
        onChange: hourOnChange,
        onArrowUp: incrementHour,
        onArrowDown: decrementHour,
        ...sharedInputProps,
        ...hourInputProps,
      }),
      [
        sharedInputProps,
        isControlled,
        value?.hours,
        hours,
        is12Hour,
        hourOnChange,
        incrementHour,
        decrementHour,
        hourInputProps,
      ],
    ),
    getMinuteInputProps: useCallback(
      () => ({
        id: MINUTES_ID,
        'data-testid': MINUTES_ID,
        value: isControlled ? value?.minutes : minutes,
        onChange: minuteOnChange,
        onArrowUp: incrementMinute,
        onArrowDown: decrementMinute,
        ...sharedInputProps,
        ...minuteInputProps,
      }),
      [
        sharedInputProps,
        isControlled,
        value?.minutes,
        minutes,
        minuteOnChange,
        incrementMinute,
        decrementMinute,
        minuteInputProps,
      ],
    ),
    showTimePeriod: is12Hour,
    getTimePeriodInputProps: useCallback(() => {
      let newTimePeriod = timePeriod;

      if (isControlled && value) {
        if (value?.hours > 11) {
          newTimePeriod = TIME_PERIODS.PM;
        } else {
          newTimePeriod = TIME_PERIODS.AM;
        }
      }

      return {
        id: TIME_PERIOD_ID,
        'data-testid': TIME_PERIOD_ID,
        value: newTimePeriod,
        onArrowUp: flipTimePeriod,
        onArrowDown: flipTimePeriod,
        isTimePeriod: true,
        onAm: setAmPeriod,
        onPm: setPmPeriod,
        ...sharedInputProps,
        ...timePeriodInputProps,
      };
    }, [
      timePeriod,
      isControlled,
      value,
      sharedInputProps,
      flipTimePeriod,
      setAmPeriod,
      setPmPeriod,
      timePeriodInputProps,
    ]),
  };
};
export default useTimePickerControl;
